import {
  BugOutlined,
  CopyOutlined,
  HomeFilled,
  LogoutOutlined,
  MenuFoldOutlined,
  MessageOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  SwapOutlined,
} from '@ant-design/icons'

import { ALERTS_URL } from 'features/alerts/paths'
import { askYogiPath } from 'features/ask-yogi/paths'
import { DASHBOARDS_2_URL } from 'features/dashboards-v2/paths'
import type { PaletteCommand } from 'features/project/features/command-palette/types'
import type { NavigationItem } from 'features/project/partials/project-layout/components/navigation/utils'
import type { SettingsNavItem } from 'features/settings/nav-config'
import { SETTINGS_URL } from 'features/settings/paths'

export type HubSummary = {
  proj_uuid: string
  title: string
  // Sub-projects are search-only so they don't flood the empty-query view.
  searchOnly?: boolean
}

// One switch entry per hub and per sub-project (group), deduped by uuid with
// the hub-level entry winning, so a pasted uuid of either kind matches.
export const collectHubSummaries = (
  projects: Array<{
    uuid: string
    title: string
    groups?: Array<{ uuid: string; title: string }> | null
  }>,
): HubSummary[] => {
  const seen = new Set<string>()
  const hubs: HubSummary[] = []
  for (const project of projects) {
    if (seen.has(project.uuid)) continue
    seen.add(project.uuid)
    hubs.push({ proj_uuid: project.uuid, title: project.title })
  }
  for (const project of projects) {
    for (const group of project.groups ?? []) {
      if (seen.has(group.uuid)) continue
      seen.add(group.uuid)
      hubs.push({ proj_uuid: group.uuid, title: group.title, searchOnly: true })
    }
  }
  return hubs
}

export type PaletteCommandInputs = {
  projectId: string
  // Already filtered to enabled items — the same list the sidebar renders.
  navigationItems: NavigationItem[]
  showHome: boolean
  // Already filtered by role allowlist and pages.hidden — same as the
  // settings shell.
  settingsItems: SettingsNavItem[]
  hubs: HubSummary[]
  canCreateDashboard: boolean
  canCreateAlert: boolean
  canOpenAskYogiAnywhere: boolean
  canNewAskYogiChat: boolean
  canSignOut: boolean
  navigate: (
    path: string,
    options?: { state?: Record<string, unknown> },
  ) => void
  switchHub: (uuid: string) => void
  toggleAskYogiAnywhere: () => void
  toggleMainNav: () => void
  signOut: () => void
  reportBug: () => void
  openHelp: () => void
  copyProjectUuid: () => void
}

export const buildPaletteCommands = (
  inputs: PaletteCommandInputs,
): PaletteCommand[] => {
  const { projectId, navigate } = inputs
  const projectBasePath = `/project/${projectId}`

  const navigationCommands: PaletteCommand[] = [
    ...(inputs.showHome
      ? [
          {
            id: 'nav:home',
            label: 'Home',
            context: ['Go to'],
            section: 'Navigation' as const,
            keywords: ['home', 'project'],
            // Same icon the sidebar's inline Home link uses.
            icon: HomeFilled,
            execute: () => navigate(projectBasePath),
          },
        ]
      : []),
    ...inputs.navigationItems.map((item) => ({
      id: `nav:${item.url}`,
      label: item.title,
      context: ['Go to'],
      section: 'Navigation' as const,
      keywords: [item.url],
      icon: item.svgComponent,
      execute: () => navigate(`${projectBasePath}/${item.url}`),
    })),
  ]

  const settingsCommands: PaletteCommand[] = inputs.settingsItems.map(
    (item) => ({
      id: `settings:${item.to}`,
      label: item.label,
      context: ['Go to Settings'],
      icon: SettingOutlined,
      section: 'Settings' as const,
      keywords: ['settings', item.to],
      execute: () => navigate(`${projectBasePath}/${SETTINGS_URL}/${item.to}`),
    }),
  )

  const actionCommands: PaletteCommand[] = [
    ...(inputs.canOpenAskYogiAnywhere
      ? [
          {
            id: 'action:ask-yogi-anywhere',
            label: 'Ask Yogi Anywhere',
            context: ['Open'],
            icon: MessageOutlined,
            section: 'Actions' as const,
            keywords: ['chat', 'assistant', 'ai'],
            execute: inputs.toggleAskYogiAnywhere,
          },
        ]
      : []),
    ...(inputs.canNewAskYogiChat
      ? [
          {
            id: 'action:new-ask-yogi-chat',
            label: 'Ask Yogi chat',
            context: ['New'],
            icon: PlusOutlined,
            section: 'Actions' as const,
            keywords: ['conversation', 'chat'],
            execute: () => navigate(askYogiPath(projectId)),
          },
        ]
      : []),
    ...(inputs.canCreateDashboard
      ? [
          {
            id: 'action:create-dashboard',
            label: 'Dashboard',
            context: ['Create'],
            icon: PlusOutlined,
            section: 'Actions' as const,
            keywords: ['new'],
            // There is no /new route — the dashboards list owns the create
            // flow (API call, then straight into the editor); this flag asks
            // it to run that flow on arrival.
            execute: () =>
              navigate(`${projectBasePath}/${DASHBOARDS_2_URL}`, {
                state: { openCreateDashboard: true },
              }),
          },
        ]
      : []),
    ...(inputs.canCreateAlert
      ? [
          {
            id: 'action:create-alert',
            label: 'Alert',
            context: ['Create'],
            icon: PlusOutlined,
            section: 'Actions' as const,
            keywords: ['new', 'notification'],
            execute: () =>
              navigate(`${projectBasePath}/${ALERTS_URL}`, {
                state: { openCreateAlert: true },
              }),
          },
        ]
      : []),
    {
      id: 'action:report-bug',
      label: 'Bug report',
      context: ['Open'],
      icon: BugOutlined,
      section: 'Actions' as const,
      keywords: ['feedback', 'issue', 'problem', 'report'],
      execute: inputs.reportBug,
    },
    {
      id: 'action:open-help',
      label: 'Help',
      context: ['Open'],
      icon: QuestionCircleOutlined,
      section: 'Actions' as const,
      keywords: ['support', 'guide', 'docs'],
      execute: inputs.openHelp,
    },
    {
      id: 'action:copy-project-uuid',
      label: 'Project UUID',
      context: ['Copy'],
      icon: CopyOutlined,
      section: 'Actions' as const,
      keywords: ['uuid', 'id', 'hub', 'clipboard'],
      // Developer tool — hidden until the user types (e.g. "copy").
      searchOnly: true,
      execute: inputs.copyProjectUuid,
    },
    {
      id: 'action:toggle-main-nav',
      label: 'Navigation sidebar',
      context: ['Toggle'],
      icon: MenuFoldOutlined,
      section: 'Actions' as const,
      keywords: ['collapse', 'expand', 'menu'],
      execute: inputs.toggleMainNav,
    },
    ...(inputs.canSignOut
      ? [
          {
            id: 'action:sign-out',
            label: 'Sign out',
            context: ['Account'],
            icon: LogoutOutlined,
            section: 'Actions' as const,
            keywords: ['logout', 'log out'],
            execute: inputs.signOut,
          },
        ]
      : []),
  ]

  const hubCommands: PaletteCommand[] = inputs.hubs
    .filter((hub) => hub.proj_uuid !== projectId)
    .map((hub) => ({
      id: `hub:${hub.proj_uuid}`,
      label: hub.title,
      context: ['Switch to'],
      icon: SwapOutlined,
      section: 'Switch hub' as const,
      // The uuid keyword lets a pasted project/hub uuid find its switch entry.
      keywords: ['hub', 'project', 'workspace', hub.proj_uuid],
      searchOnly: hub.searchOnly,
      execute: () => inputs.switchHub(hub.proj_uuid),
    }))

  return [
    ...navigationCommands,
    ...settingsCommands,
    ...actionCommands,
    ...hubCommands,
  ]
}
